import { useEffect, useState } from "react";

import { getHeaders } from "../../api";
import { useGetToken } from "../../authConfig";

interface Props {
    src?: string;
    alt?: string;
}

// Blob URLs are cached per src so re-renders and remounts don't refetch or flicker.
const blobUrlCache = new Map<string, string>();

// <img> can't send the bearer token, so /content/ images are fetched and shown as blob URLs.
export const AuthImage = ({ src, alt }: Props) => {
    const token = useGetToken();
    const isContent = !!src?.startsWith("/content/");
    const [objectUrl, setObjectUrl] = useState<string | undefined>(src ? blobUrlCache.get(src) : undefined);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!src || !isContent) return;
        const cached = blobUrlCache.get(src);
        if (cached) {
            setObjectUrl(cached);
            return;
        }
        let cancelled = false;
        fetch(src, { headers: getHeaders(token) })
            .then(r => (r.ok ? r.blob() : Promise.reject(new Error(`${r.status}`))))
            .then(blob => {
                const objUrl = URL.createObjectURL(blob);
                blobUrlCache.set(src, objUrl);
                if (!cancelled) setObjectUrl(objUrl);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [src, token]);

    if (!isContent) return <img src={src} alt={alt} />;
    if (failed) return <span>{alt}</span>;
    // Reserved height avoids scroll jumps while the image loads.
    return objectUrl ? <img src={objectUrl} alt={alt} /> : <div style={{ minHeight: 300 }} />;
};
